import React from "react";
import "./style.min.css";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FiCode, FiSearch, FiShield, FiArrowRight } from "react-icons/fi";
import { CALENDLY_URL } from "../../constants/links";
import { trackSeoEvent } from "../../helpers/analytics";

const Services = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const cards = [
    {
      icon: <FiCode size={28} />,
      title: t("services.webDev.title"),
      desc: t("services.webDev.desc"),
      gradient: "var(--hover)",
      link: "/creation-site-web",
    },
    {
      icon: <FiSearch size={28} />,
      title: t("services.seo.title"),
      desc: t("services.seo.desc"),
      gradient: "linear-gradient(135deg, var(--secondary), var(--accent))",
      link: "/nodejs-nestjs",
    },
    {
      icon: <FiShield size={28} />,
      title: t("services.maintenance.title"),
      desc: t("services.maintenance.desc"),
      gradient: "linear-gradient(135deg, var(--primary), var(--accent))",
      link: "/audit-seo-technique",
    },
  ];

  const relatedServices = [
    { label: "Full Stack freelance", path: "/full-stack-freelance" },
    { label: "Application web", path: "/application-web" },
    { label: "Développement SaaS", path: "/developpement-saas" },
    { label: "PWA", path: "/pwa" },
    { label: "React", path: "/developpeuse-react-seo" },
    { label: "Angular", path: "/developpeuse-angular-seo" },
    { label: "Performance web", path: "/optimisation-performance-web" },
  ];

  const BUTTONS = [
    { text: t("hero.cta_quote"), link: "/quote" },
    { text: t("hero.cta_call"), link: CALENDLY_URL, external: true },
    { text: t("contact_title"), link: "/contact" },
  ];

  return (
    <section
      id="services"
      dir={lang === "ar" ? "rtl" : "ltr"}
      className="py-24 px-4 sm:px-6 lg:px-24 max-w-7xl mx-auto"
      style={{
        background: "var(--body_background)",
        color: "var(--color_text)",
      }}
    >
      {/* Section Header */}
      <div className="text-center mb-16 sm:mb-20">
        <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-4 leading-tight  text-[var(--accent)]">
          {t("services.title")}
        </h2>
        <p className="text-base sm:text-lg text-[var(--body_color)] max-w-2xl mx-auto">
          {t("services.subtitle")}
        </p>
      </div>

      {/* Service Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
        {cards.map((card, index) => {
          const button = BUTTONS[index % BUTTONS.length];

          return (
            <div
              key={index}
              className="relative group p-[2px] rounded-[24px] transition-transform duration-300 hover:scale-[1.015]"
              style={{
                backgroundImage: card.gradient,
              }}
            >
              <div
                className="relative z-10 rounded-[22px] p-6 sm:p-8 h-full pulse-hover flex flex-col"
                style={{
                  background: "var(--background_box)",
                  boxShadow: "var(--box-shadow)",
                  backdropFilter: "var(--glass-blur)",
                }}
              >
                <div
                  className="w-14 h-14 mb-6 flex items-center justify-center rounded-xl text-white shadow-md transition-transform group-hover:scale-110"
                  style={{ backgroundImage: card.gradient }}
                >
                  {card.icon}
                </div>

                <h3
                  className="text-xl sm:text-2xl font-semibold mb-3"
                  style={{ color: "var(--color_text)" }}
                >
                  {card.title}
                </h3>

                <p
                  className="text-sm sm:text-base leading-relaxed mb-6 flex-grow"
                  style={{ color: "var(--body_color)" }}
                >
                  {card.desc}
                </p>

                <Link
                  to={card.link}
                  className="mt-auto inline-flex items-center font-semibold text-[var(--accent)] hover:opacity-80 transition"
                >
                  {button.text}
                  <FiArrowRight className="ml-2" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 flex flex-wrap justify-center gap-3">
        {relatedServices.map((service) => (
          <Link
            key={service.path}
            to={service.path}
            className="rounded-full border border-[var(--neutral)] bg-[var(--background_box)] px-4 py-2 text-sm font-semibold text-[var(--body_color)] transition hover:-translate-y-1 hover:text-[var(--accent)]"
          >
            {service.label}
          </Link>
        ))}
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackSeoEvent("booking_click", { label: "services_home" })}
          className="rounded-full border border-[var(--neutral)] bg-[var(--background_box)] px-4 py-2 text-sm font-semibold text-[var(--body_color)] transition hover:-translate-y-1 hover:text-[var(--accent)]"
        >
          Réserver un appel
        </a>
      </div>
    </section>
  );
};

export default Services;
